import React from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  VStack,
  Text,
  Heading,
  HStack,
  Badge,
  IconButton,
  Icon,
  Box,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, ViewIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { FaTasks } from 'react-icons/fa';
import { PermissionGuard } from '../PermissionGuard';
import { Permission, canModifyProject } from '../../utils/permissions';
import ProjectModal from './modals/ProjectModal';
import { CreateProjectData } from '../../api/projectApi';
import { CreateTaskData } from '../../api/taskApi';
import { useUpdateProject, useArchiveProject } from '../../hooks/useProjects';
import { useCreateTask, useUpdateTask, useDeleteTask } from '../../hooks/useTasks';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';

interface ProjectCardProps {
  project: any;
  projectTasks: any[];
  isExpanded: boolean;
  completedTasks: number;
  urgentTasks: number;
  onEdit: (updatedProjectData: any) => void;
  onDelete: () => void;
  onView: () => void;
  onToggleExpand: () => void;
  onAddTask: () => void;
  getStatusIcon: (status: string) => any;
  getStatusColor: (status: string) => string;
  getPriorityColor: (priority: string) => string;
  borderColor: string;
  expandButtonBg: string;
  taskContainerBg: string;
  taskItemBg: string;
  getClientName: (clientData: any) => string;
  clients: any[];
}

// Helper function to strip HTML tags
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  projectTasks,
  isExpanded,
  completedTasks,
  urgentTasks,
  onEdit,
  onDelete,
  onView,
  onToggleExpand,
  onAddTask,
  getStatusIcon,
  getStatusColor,
  getPriorityColor,
  borderColor,
  expandButtonBg,
  taskContainerBg,
  taskItemBg,
  getClientName,
  clients,
}) => {
  const [isProjectModalOpen, setProjectModalOpen] = React.useState(false);
  const [editingProject, setEditingProject] = React.useState<any>(null);
  const [formData, setFormData] = React.useState<CreateProjectData | any>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const { data: organizationEnums } = useOrganizationEnums();
  const projectTypes = organizationEnums?.projectTypes ?? [];
  // clients is now received as a prop

  const updateProjectMutation = useUpdateProject();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const archiveProjectMutation = useArchiveProject();

  const archivedBg = useColorModeValue('gray.100', 'gray.700');
  const normalBg = useColorModeValue('white', 'gray.50');

  const { authenticatedUser } = useAuthenticatedUser();
  const isProjectOwner = authenticatedUser && project.userWhoCreatedProject && authenticatedUser.LoggedInUserId === project.userWhoCreatedProject._id;
  // Check if user is assigned to this project
  const isUserAssigned = React.useMemo(() => {
    if (!authenticatedUser || !project.assignedToUser) return false;
    return Array.isArray(project.assignedToUser)
      ? project.assignedToUser.some((u: any) => {
          if (typeof u === 'string') return u === authenticatedUser.LoggedInUserId;
          if (u && typeof u === 'object' && (u._id || u.$oid)) {
            return u._id === authenticatedUser.LoggedInUserId || u.$oid === authenticatedUser.LoggedInUserId;
          }
          return false;
        })
      : false;
  }, [authenticatedUser, project.assignedToUser]);
  const canEditOrDeleteOrArchiveOrAddTask = canModifyProject(
    authenticatedUser,
    !!isProjectOwner,
    isUserAssigned
  );

  const handleEdit = () => {
    setEditingProject(project);
    // Ensure client is a string id for the form
    let clientId = '';
    if (project.client) {
      if (typeof project.client === 'string') {
        clientId = project.client;
      } else if (project.client._id) {
        clientId = project.client._id;
      } else if (project.client.$oid) {
        clientId = project.client.$oid;
      }
    }
    // Normalize authorizedBy to array of string IDs
    let authorizedBy: string[] = [];
    if (Array.isArray(project.authorizedBy)) {
      authorizedBy = project.authorizedBy.map((u: any) => typeof u === 'string' ? u : u._id);
    }
    let assignedToUser: string[] = [];
    if (Array.isArray(project.assignedToUser)) {
      assignedToUser = project.assignedToUser.map((u: any) =>
        typeof u === 'string'
          ? u
          : u._id || u.$oid || (u && u.toString && u.toString()) || ''
      );
    }
    setFormData({ ...project, client: clientId, authorizedBy, assignedToUser });
    setProjectModalOpen(true);
  };

  // Replace handleSave with API logic
  const handleSave = async (projectData: CreateProjectData, tasks: Partial<CreateTaskData & { _id?: string }>[]) => {
    setIsLoading(true);
    try {
      // Ensure client is a string
      const clientId = typeof projectData.client === 'string' ? projectData.client : (projectData.client && (projectData.client as any)._id) || '';
      // Normalize authorizedBy to array of string IDs
      const authorizedBy = Array.isArray(projectData.authorizedBy)
        ? projectData.authorizedBy.map((u: any) => typeof u === 'string' ? u : u._id)
        : [];
      await updateProjectMutation.mutateAsync({
        id: project._id,
        data: {
          ...projectData,
          client: clientId,
          authorizedBy,
        },
      });
      // Handle tasks CRUD (create/update)
      for (const task of tasks) {
        const taskId = (task as any)._id;
        if (!taskId) {
          // Only create if required fields are present
          if (task.taskTitle && task.description) {
            await createTaskMutation.mutateAsync({
              ...task,
              project: project._id,
              taskTitle: task.taskTitle,
              description: task.description,
              priority: task.priority || 'medium',
              type: task.type || 'development',
              status: task.status || 'In progress',
              client: clientId,
            } as CreateTaskData);
          }
        } else {
          await updateTaskMutation.mutateAsync({ id: taskId, data: task });
        }
      }
      setFormData({ ...projectData });
      setEditingProject({ ...editingProject, ...projectData });
    } catch (err) {
      // Optionally handle error
    }
    setTimeout(() => {
      setIsLoading(false);
      setProjectModalOpen(false);
    }, 1000);
  };

  const handleArchiveToggle = async () => {
    if (!project._id) return;
    await archiveProjectMutation.mutateAsync({ id: project._id, archived: !project.archived });
  };

  if (project.archived) {
    return (
      <Card key={project._id} variant="elevated" bg={archivedBg} opacity={0.7}>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1} width="full">
            <HStack spacing={2} justifyContent={"space-between"} width="full"> 
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {project.projectNumber}
              </Text>
              <Button
                size="sm"
                variant="outline"
                colorScheme="orange"
                onClick={handleArchiveToggle}
                isLoading={archiveProjectMutation.isPending}
              >
                Unarchive
              </Button>
            </HStack>
              <Heading size="md" noOfLines={1} color="gray.600">
               <VStack spacing={0} align={"start"}>
                  <Badge ml={2} colorScheme="gray" variant="solid" px={1} mx={0}>Archived</Badge>
                  <span>{project.projectName}</span>
                </VStack>
              </Heading>
            </VStack>
          </Flex>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="start" spacing={3}>
            <Text fontSize="sm" fontWeight="medium">
              Client: {getClientName(project.client)}
            </Text>
            <Text fontSize="sm" color="gray.600">
              Created: {new Date(project.createdAt).toLocaleDateString()}
            </Text>
            <Text fontSize="sm" color="gray.600">
              By: {project.userWhoCreatedProject.firstName}{' '}
              {project.userWhoCreatedProject.lastName}
            </Text>
            <Text fontSize="md" color="gray.500" mt={2}>
              This project is archived and cannot be edited or have tasks added.<br />
              You can unarchive it to make changes.
            </Text>
            <HStack spacing={2} wrap="wrap">
              <Badge colorScheme="purple" variant="subtle">
                {project.type}
              </Badge>
              <Badge colorScheme="blue" variant="subtle">
                {projectTasks.length} task{projectTasks.length !== 1 ? 's' : ''}
              </Badge>
              {completedTasks > 0 && (
                <Badge colorScheme="green" variant="subtle">
                  {completedTasks} completed
                </Badge>
              )}
              {urgentTasks > 0 && (
                <Badge colorScheme="red" variant="subtle">
                  {urgentTasks} urgent
                </Badge>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card key={project._id} variant="elevated" bg={normalBg} opacity={project.archived ? 0.5 : 1}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
         
           <VStack align="start" spacing={1} w={'full'} gap={2}>
          <HStack spacing={2} w={'full'} justifyContent={"space-between"}>

                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="medium">
              {project.projectNumber}
            </Text>
                    </Box>
                    <Box>
            <IconButton
              aria-label="View project overview"
              icon={<ViewIcon />}
              size="sm"
              variant="ghost"
              onClick={onView}
            />
            <PermissionGuard permission={Permission.EDIT_PROJECTS}>
              <IconButton
                aria-label="Edit project"
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                onClick={handleEdit}
                isDisabled={!canEditOrDeleteOrArchiveOrAddTask}
              />
            </PermissionGuard>
            <PermissionGuard permission={Permission.DELETE_PROJECTS}>
              <IconButton
                aria-label="Delete project"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={onDelete}
                isDisabled={!canEditOrDeleteOrArchiveOrAddTask}
              />
            </PermissionGuard>
            <Button
              size="sm"
              variant={project.archived ? 'outline' : 'solid'}
              colorScheme={project.archived ? 'gray' : 'orange'}
              onClick={handleArchiveToggle}
              isLoading={archiveProjectMutation.isPending}
              isDisabled={!canEditOrDeleteOrArchiveOrAddTask}
            >
              {project.archived ? 'Unarchive' : 'Archive'}
            </Button>
            </Box>
          </HStack>

           <VStack align="start" spacing={1} w={'full'}>
    
            <Heading size="md" noOfLines={2} color="gray.600">
              {project.projectName}
              {project.archived && (
                <Badge ml={2} colorScheme="gray" variant="solid">Archived</Badge>
              )}
            </Heading>
          </VStack>
          </VStack>



        </Flex>
      </CardHeader>
      <CardBody pt={2}>
        <VStack align="start" spacing={3}>
          <HStack spacing={2} wrap="wrap">
            <Badge colorScheme="purple" variant="subtle">
              {project.type}
            </Badge>
            <Badge colorScheme="blue" variant="subtle">
              {projectTasks.length} task{projectTasks.length !== 1 ? 's' : ''}
            </Badge>
            {completedTasks > 0 && (
              <Badge colorScheme="green" variant="subtle">
                {completedTasks} completed
              </Badge>
            )}
            {urgentTasks > 0 && (
              <Badge colorScheme="red" variant="subtle">
                {urgentTasks} urgent
              </Badge>
            )}
          </HStack>

          <Text fontSize="sm" fontWeight="medium">
            Client: {getClientName(project.client)}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Created: {new Date(project.createdAt).toLocaleDateString()}
          </Text>
          <Text fontSize="sm" color="gray.600">
            By: {project.userWhoCreatedProject.firstName}{' '}
            {project.userWhoCreatedProject.lastName}
          </Text>

          {/* Tasks section */}
          {projectTasks.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                width="full"
                justifyContent="space-between"
                rightIcon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                onClick={onToggleExpand}
                bg={expandButtonBg}
              >
                View Tasks ({projectTasks.length})
              </Button>

              {isExpanded && (
                <Box
                  width="full"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={3}
                  bg={taskContainerBg}
                >
                  <VStack spacing={2} align="stretch">
                    {projectTasks.slice(0, 5).map(task => (
                      <Box
                        key={task._id}
                        p={2}
                        bg={taskItemBg}
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderLeftColor={
                          task.priority === 'urgent'
                            ? 'red.400'
                            : task.priority === 'high'
                              ? 'orange.400'
                              : task.priority === 'medium'
                                ? 'yellow.400'
                                : 'green.400'
                        }
                      >
                        <Flex justify="space-between" align="start" mb={1}>
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                              {task.poNumber}
                            </Text>
                            <Text fontSize="xs" color="gray.600" noOfLines={2}>
                              {stripHtmlTags(task.description)}
                            </Text>
                          </VStack>
                          <Icon
                            as={getStatusIcon(task.status)}
                            color={
                              task.status === 'Completed'
                                ? 'green.500'
                                : task.status === 'In progress'
                                  ? 'blue.500'
                                  : task.status === 'On hold'
                                    ? 'red.500'
                                    : 'gray.500'
                            }
                            ml={2}
                          />
                        </Flex>
                        <HStack spacing={1} justify="space-between">
                          <HStack spacing={1}>
                            <Badge
                              size="xs"
                              colorScheme={getStatusColor(task.status)}
                              variant="subtle"
                            >
                              {task.status}
                            </Badge>
                            <Badge
                              size="xs"
                              colorScheme={getPriorityColor(task.priority)}
                              variant="outline"
                            >
                              {task.priority}
                            </Badge>
                          </HStack>
                          {task.quotedPrice && (
                            <Text fontSize="xs" color="green.600" fontWeight="bold">
                              ${task.quotedPrice.toLocaleString()}
                            </Text>
                          )}
                        </HStack>
                      </Box>
                    ))}
                    {projectTasks.length > 5 && (
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        +{projectTasks.length - 5} more tasks
                      </Text>
                    )}
                  </VStack>
                </Box>
              )}
            </>
          )}

          <PermissionGuard permission={Permission.CREATE_TASKS}>
            <Button
              leftIcon={<Icon as={FaTasks} />}
              colorScheme="blue"
              variant="outline"
              size="sm"
              width="full"
              mt={3}
              onClick={onAddTask}
              isDisabled={!canEditOrDeleteOrArchiveOrAddTask}
            >
              Add Task
            </Button>
          </PermissionGuard>
        </VStack>
      </CardBody>
      <ProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setProjectModalOpen(false)}
        editingProject={editingProject}
        onSave={handleSave}
        isLoading={isLoading}
        formData={formData}
        setFormData={setFormData}
        projectTypes={projectTypes}
        clients={clients}
        projectTasks={projectTasks}
      />
    </Card>
  );
};

export default ProjectCard;
